import axios from "axios";
import { notifyError } from "common/utils/app-monitoring/notify";
import { dropEmptyKeys } from "../core";
import { isSessionExpired, refreshCsrfToken } from "../csrf-token-manager";
import getMetaEnv from "../meta";
import { asyncRetryIfTransientError, fetchWithRetry } from "../retries";
import isSameOriginRequest from "../same-origin";
import { withCache } from "./cache";
import buildRequestFingerprint from "./build-request-fingerprint";
import { isAxiosNetworkError, shouldReportError } from "./error-classification";

export { isAxiosNetworkError, shouldReportError, buildRequestFingerprint };

const withCsrfRetry = async (requestFn, source) => {
  try {
    return await requestFn();
  } catch (error) {
    if (!isSessionExpired(error)) throw error;

    const refreshed = await refreshCsrfToken(source);
    if (!refreshed) {
      throw error;
    }

    return requestFn();
  }
};

const AXIOS_DEFAULT_TIMEOUT = 15_000;

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

const defaultHeaders = () => ({
  "X-CSRF-Token": getMetaEnv("csrf-token"),
  "X-Requested-With": "XMLHttpRequest",
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8"
});

const buildParams = ({ headers = {}, ...rest }, method) => ({
  method,
  headers: dropEmptyKeys({
    ...defaultHeaders(),
    ...headers
  }),
  credentials: "same-origin",
  ...rest
});

// HB-1926: include request URL/method/status in every Honeybadger notice so we
// can identify which endpoint is failing. `responseSnippet` is truncated at 200
// chars; pass `responseBody` as a string (or undefined to skip the snippet).
// HB-1942: also pass a stable fingerprint as the top-level HB grouping field
// so the vendor-frame backtrace collapse doesn't merge async-fetch errors
// from different endpoints into one bucket.
const reportRequestError = (error, { url, method, responseBody, silent }) => {
  if (silent) return;
  try {
    if (!shouldReportError(error)) return;
    const httpStatus = error.status ?? error.response?.status;
    const emptyBody = responseBody === "";
    if (httpStatus >= 500 && emptyBody && /\/api\/v2\/estimates\//.test(url || "")) return;

    const fingerprint = buildRequestFingerprint(method, url, httpStatus);
    notifyError(error, {
      context: {
        requestUrl: url,
        requestMethod: method,
        httpStatus,
        responseSnippet: typeof responseBody === "string" ? responseBody.slice(0, 200) : undefined
      },
      ...(fingerprint != null ? { fingerprint } : {})
    });
  } catch (err) {
    notifyError("Error has malformed data", {
      context: { requestUrl: url, requestMethod: method }
    });
  }
};

const haltErrorResponse = async response => {
  if (response.status >= 400 && response.status <= 599) {
    let errorMessage;
    let responseData;
    const textBody = await response.text();

    // Try to parse JSON response body
    try {
      responseData = JSON.parse(textBody);
    } catch {
      responseData = textBody;
    }

    if (textBody) {
      errorMessage =
        response.status === 422
          ? textBody
          : `${response.statusText || `HTTP ${response.status}`}: ${textBody}`;
    } else {
      errorMessage = response.statusText || `HTTP ${response.status}`;
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    error.message = errorMessage;
    error.response = responseData; // Preserve the parsed response data
    throw error;
  }
  return response;
};

const increaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active += 1;
};

const decreaseAsyncRequestsCount = url => {
  if (isSameOriginRequest(url)) window.ajax_active -= 1;
};

const sendBeacon = (url, data) =>
  new Promise((resolve, reject) => {
    const dataString = JSON.stringify(data);
    const queued = navigator.sendBeacon(url, dataString);

    if (queued) {
      resolve();
    } else {
      reject(new Error(`Failed to queue data for sending to ${url}`));
    }
  });

const executeFetch = (url, params, method) =>
  fetchWithRetry(url, buildParams(params, method))
    .then(haltErrorResponse)
    .then(async response => {
      if (response.status === 204) return undefined;
      const text = await response.text();
      if (!text) return undefined;
      try {
        return JSON.parse(text);
      } catch (parseError) {
        const error = new Error(`Non-JSON response for ${method} ${url}: ${text.slice(0, 80)}`);
        error.status = response.status;
        error.response = text;
        error.cause = parseError;
        throw error;
      }
    });

// `silent: true` opt-in skips notifyError() for known-noisy probes/polls
// (e.g. DTF artwork enhancement acknowledgement, jiffystickers composition
// lookup). The error is still rethrown so caller .catch handlers run; only
// the Honeybadger notify pipe is suppressed. See HB-1851.
const request = (url, params = {}, method = "GET") => {
  const { silent, ...rest } = params;
  increaseAsyncRequestsCount(url);

  if (rest.sendBeacon) {
    delete rest.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, rest);
  }

  const fetchPromise = MUTATION_METHODS.includes(method.toUpperCase())
    ? withCsrfRetry(() => executeFetch(url, rest, method), "request_layer")
    : executeFetch(url, rest, method);

  return fetchPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response, silent });
      // Throw a network error only when there is no HTTP status code (mirrors
      // the guard in shouldReportError to avoid swallowing server error bodies).
      const httpStatus = error.status ?? error.response?.status;
      if (!httpStatus && isAxiosNetworkError(error)) {
        throw new Error("Unable to connect to server");
      }
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

/**
 * A non-native implementation of request method
 * You should use default export unless you need specific functions like progress tracking
 * @param url
 * @param params
 * @param method
 * @returns {Promise<void>|Promise<unknown>}
 */
const executeAxios = (url, params, method) => {
  const { timeout, ...axiosParams } = buildParams(params, method);
  return axios(url, {
    ...axiosParams,
    timeout: timeout ?? AXIOS_DEFAULT_TIMEOUT
  })
    .then(haltErrorResponse)
    .then(response => response.data);
};

export const axiosRequest = (url, params = {}, method = "GET") => {
  increaseAsyncRequestsCount(url);
  const { noRetry, silent, ...requestParams } = params;

  if (requestParams.sendBeacon) {
    delete requestParams.sendBeacon;
    decreaseAsyncRequestsCount(url);
    return sendBeacon(url, requestParams);
  }

  const upperMethod = method.toUpperCase();
  const isGet = upperMethod === "GET";
  const execute = () =>
    MUTATION_METHODS.includes(upperMethod)
      ? withCsrfRetry(() => executeAxios(url, requestParams, method), "axios_request_layer")
      : executeAxios(url, requestParams, method);

  const shouldRetry = isGet && !noRetry;
  const axiosPromise = shouldRetry
    ? asyncRetryIfTransientError(execute, { errorMatch: isAxiosNetworkError })
    : execute();

  return axiosPromise
    .catch(error => {
      reportRequestError(error, { url, method, responseBody: error.response?.data, silent });
      throw error;
    })
    .finally(() => decreaseAsyncRequestsCount(url));
};

export default withCache(request);
